import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Radio, Users, Crown } from "lucide-react";

/**
 * LiveNowPanel — a public/inline widget that lists every instant live
 * broadcast currently in progress.
 *
 * Renders on:
 *   • /golive Studio (for seekers and providers browsing before hosting)
 *   • /livestreams (as the "Live Now" tab)
 *
 * Polls `/api/live/active` every 20 seconds so it feels alive without
 * spamming the backend. Auto-hides the widget when nothing is live —
 * we don't want to broadcast "0 practitioners live right now" to seekers.
 */
export default function LiveNowPanel({ heading = "Live Now", hideWhenEmpty = false }) {
  const [lives, setLives] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/live/active");
        if (!alive) return;
        setLives(data.lives || []);
      } catch { /* silent — polling; toast would be annoying */ }
      finally { if (alive) setLoading(false); }
    };
    load();
    const t = setInterval(load, 20_000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (hideWhenEmpty && !loading && lives.length === 0) return null;

  return (
    <div className="parchment p-5" data-testid="live-now-panel">
      <div className="flex items-baseline justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-primary pulse-ember" />
          <span className="label-mystic text-secondary">{heading}</span>
          {lives.length > 0 && (
            <span className="text-[10px] uppercase tracking-wider text-foreground/50" data-testid="live-now-count">
              · {lives.length} broadcast{lives.length === 1 ? "" : "s"}
            </span>
          )}
        </div>
      </div>
      {loading ? (
        <div className="text-sm text-foreground/55">Checking who is live…</div>
      ) : lives.length === 0 ? (
        <div className="text-sm text-foreground/55">
          No one is broadcasting right now. When a practitioner goes live, they&apos;ll appear here.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {lives.map((l) => (
            <Link
              key={l.id}
              to={l.joinUrl}
              className="border border-secondary/25 hover:border-primary/60 hover:bg-primary/5 transition rounded-sm p-4 flex gap-3 items-start"
              data-testid={`live-now-card-${l.hostId}`}
            >
              <div className="relative shrink-0">
                {l.hostAvatarUrl ? (
                  <img src={l.hostAvatarUrl} alt="" className="w-12 h-12 rounded-full object-cover border border-secondary/30" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-input border border-secondary/30 flex items-center justify-center font-serif text-secondary">
                    {(l.hostName || "?")[0].toUpperCase()}
                  </div>
                )}
                {l.hostElder && (
                  <Crown className="absolute -top-1 -right-1 w-4 h-4 text-primary fill-primary/40" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-serif text-base truncate">{l.hostName}</div>
                {l.title && <div className="text-xs text-foreground/60 truncate">{l.title}</div>}
                <div className="mt-2 flex items-center gap-2 text-[10px] uppercase tracking-wider">
                  <span className="inline-flex items-center gap-1 text-primary">
                    <Radio className="w-3 h-3" /> Live
                  </span>
                  <span className="text-foreground/45">Tap to join</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
